/* ───────────────────────────────────────────────────────────────────────────
 * lcsViewModel.js — pure adapter
 *
 * Converts a staged-overlay response (the JSON shape returned by
 * /api/overlay_staged.php) into a normalized view-model the
 * ForecastDashboardDecisionUI can consume without further parsing.
 *
 * Pure function only. No fetches. No React. No side effects.
 * Safe to call in any environment (browser, node, web-worker).
 *
 * Input shape (subset):
 *   {
 *     ok: true,
 *     actual:    { Oil: [{date, q}, ...], Gas: [...], Water: [...] },
 *     forecast:  { Oil: [{date, p10, p50, p90}, ...], Gas: [...], Water: [...] },
 *     anomalies: { Oil: [...], Gas: [...], Water: [...] },
 *     params:    { Gas: { method, level, n_positive, n_total, ... } },
 *     metrics:   { Gas: { train: {mape, rmse_log}, holdout: {...}, aic } },
 *     meta:      { tenant_id, lease, horizon, model_version, api_version,
 *                  forecast_start, forecast_end, created_at, ... }
 *   }
 *
 * Output shape: see buildLcsViewModel() return statement.
 * ──────────────────────────────────────────────────────────────────────── */

const STREAMS = ["Oil", "Gas", "Water"];
const DAY_MS = 86400000;

const finite = (n) => Number.isFinite(n);

function meanOfPositives(arr) {
  if (!arr || !arr.length) return null;
  let sum = 0, n = 0;
  for (const v of arr) {
    if (finite(v) && v > 0) { sum += v; n++; }
  }
  return n ? sum / n : null;
}

function tail(arr, k) {
  if (!arr || !arr.length) return [];
  return arr.slice(Math.max(0, arr.length - k));
}

function parseDate(s) {
  if (!s || typeof s !== "string") return NaN;
  const t = Date.parse(s.length === 10 ? s + "T00:00:00Z" : s);
  return Number.isFinite(t) ? t : NaN;
}

function ageDays(isoLike) {
  const t = parseDate(isoLike);
  if (!Number.isFinite(t)) return null;
  return Math.max(0, Math.round((Date.now() - t) / DAY_MS));
}

function profileStreamActual(rows) {
  const points = Array.isArray(rows) ? rows : [];
  const vals = points.map((p) => (p && finite(p.q) ? p.q : null));
  const positive = vals.filter((v) => v != null && v > 0);
  const zeros = vals.filter((v) => v === 0).length;
  const nulls = vals.filter((v) => v === null).length;
  const last7 = tail(vals, 7).filter((v) => v != null && v > 0);
  const last30 = tail(vals, 30).filter((v) => v != null && v > 0);
  const last7Mean = meanOfPositives(last7);
  const last30Mean = meanOfPositives(last30);
  const trendPct =
    last7Mean != null && last30Mean != null && last30Mean > 0
      ? last7Mean / last30Mean - 1
      : null;
  const latestPositive = [...positive].slice(-1)[0] ?? null;
  return {
    count: points.length,
    positiveCount: positive.length,
    zeroCount: zeros,
    nullCount: nulls,
    allZero: positive.length === 0 && points.length > 0,
    latest: latestPositive,
    last7Mean,
    last30Mean,
    trendPct,
    firstDate: points[0]?.date ?? null,
    lastDate: points[points.length - 1]?.date ?? null,
  };
}

function profileStreamForecast(rows) {
  const points = Array.isArray(rows) ? rows : [];
  if (!points.length) return null;
  const p50s = points.map((p) => p?.p50).filter(finite);
  const flat =
    p50s.length >= 2 &&
    Math.abs(p50s[p50s.length - 1] - p50s[0]) < 1e-6;
  return {
    count: points.length,
    firstDate: points[0]?.date ?? null,
    lastDate: points[points.length - 1]?.date ?? null,
    p50First: p50s[0] ?? null,
    p50Last: p50s[p50s.length - 1] ?? null,
    p50Min: p50s.length ? Math.min(...p50s) : null,
    p50Max: p50s.length ? Math.max(...p50s) : null,
    flat,
  };
}

function streamConfidence({ actual, forecast, params, metrics }) {
  // Mirrors the backend's compute_stream_metadata heuristic, simplified.
  if (!actual || actual.allZero) return "n/a";
  if (!forecast) return "low";
  const mape = metrics?.train?.mape;
  const dense =
    actual.positiveCount >= 45 && actual.zeroCount / Math.max(1, actual.count) <= 0.35;
  if (dense && finite(mape) && mape <= 0.25) return "high";
  if ((finite(mape) && mape <= 0.5) || actual.positiveCount >= 15) return "medium";
  return "low";
}

function streamRankingScore({ actual, forecast, params, metrics, name }) {
  // Deterministic: higher = more attention. No randomness.
  if (!actual || actual.count === 0) return 0;
  if (actual.allZero) return name === "Oil" ? 35 : 25; // inactive gets a moderate flag
  const decline = -(actual.trendPct ?? 0); // positive when declining
  const sparsity = 1 - actual.positiveCount / Math.max(1, actual.count);
  const mape = metrics?.train?.mape ?? 0.5;
  // Weight: decline * 60 + sparsity * 25 + mape * 15, clamped 0–99
  const raw = Math.max(0, decline) * 60 + sparsity * 25 + Math.min(1, mape) * 15;
  return Math.max(5, Math.min(99, Math.round(40 + raw * 60)));
}

function streamRankingLabel({ actual, forecast, params, name }) {
  if (!actual || actual.count === 0) return "No data";
  if (actual.allZero) return `Inactive — no ${name.toLowerCase()} produced`;
  if (!forecast) return "Insufficient data to forecast";
  const method = params?.method;
  const decline = actual.trendPct;
  if (method === "sparse_intermittent")
    return finite(decline) && decline < -0.2
      ? `Sparse + declining (${(decline * 100).toFixed(0)}%)`
      : "Sparse / intermittent";
  if (finite(decline) && decline < -0.1) return `Declining ${(decline * 100).toFixed(0)}%`;
  if (finite(decline) && decline > 0.05) return `Growing ${(decline * 100).toFixed(0)}%`;
  return "Stable";
}

function buildDecisionContext(streamProfiles) {
  const active = streamProfiles.filter((s) => s.present);
  const inactive = streamProfiles.filter((s) => !s.present).map((s) => s.name);
  const allInactive = active.length === 0;

  // Most-attention stream drives the headline action.
  const primary = [...streamProfiles].sort((a, b) => b.ranking.score - a.ranking.score)[0];
  const declining = active.filter((s) => finite(s.actual.trendPct) && s.actual.trendPct < -0.15);

  let suggestedAction = "MONITOR";
  let actionColor = "yellow";
  let actionWhy = "Continue monitoring under current assumptions.";
  let confidence = "MODERATE";

  if (allInactive) {
    suggestedAction = "EVALUATE SHUT-IN";
    actionColor = "red";
    actionWhy = "No positive production observed across any stream in the available window.";
    confidence = "HIGH";
  } else if (primary?.actual?.allZero === false && declining.length === active.length) {
    suggestedAction = "EVALUATE SHUT-IN";
    actionColor = "red";
    actionWhy = `All active streams are declining (${declining.map((s) => s.name).join(", ")}). Forecast is flat or downward.`;
    confidence = "HIGH";
  } else if (declining.length > 0) {
    suggestedAction = "INVESTIGATE DECLINE";
    actionColor = "yellow";
    actionWhy = `Declining trend on ${declining.map((s) => s.name).join(", ")}. Check mechanical and reservoir status.`;
    confidence = "MODERATE";
  } else {
    suggestedAction = "MAINTAIN PROGRAM";
    actionColor = "green";
    actionWhy = "Active streams stable; forecast within calibrated bands.";
    confidence = "MODERATE";
  }

  return {
    activeStreamCount: active.length,
    inactiveStreams: inactive,
    primaryStream: primary?.name ?? null,
    suggestedAction,
    actionColor,
    actionWhy,
    confidence,
    streamConfidences: streamProfiles.reduce((m, s) => {
      m[s.name] = s.confidence;
      return m;
    }, {}),
  };
}

function buildCaveats({ modelInfo, streamProfiles, ok }) {
  const c = [];
  if (!ok) c.push("Upstream returned ok=false; data may be partial.");
  if (modelInfo?.staleness?.ageDays != null && modelInfo.staleness.ageDays > 7) {
    c.push(
      `Forecast generated ${modelInfo.staleness.ageDays} days ago — consider refreshing the backfill.`,
    );
  }
  const inactive = streamProfiles.filter((s) => s.actual?.allZero).map((s) => s.name);
  if (inactive.length) {
    c.push(
      `${inactive.join(" and ")} stream${inactive.length > 1 ? "s have" : " has"} zero positive observations — model intentionally did not fit ${inactive.length > 1 ? "them" : "it"}.`,
    );
  }
  for (const s of streamProfiles) {
    if (!s.present) continue;
    if (s.params?.method === "sparse_intermittent") {
      c.push(
        `${s.name} forecast uses the sparse-intermittent method (event-rate hurdle, flat level) due to low data density.`,
      );
    }
    const mape = s.metrics?.train?.mape;
    if (finite(mape) && mape > 0.5) {
      c.push(
        `${s.name} train MAPE ${(mape * 100).toFixed(0)}% reflects high uncertainty — rely on p10/p90 bands rather than p50.`,
      );
    }
  }
  return c;
}

/**
 * Build a normalized DecisionUI view-model from a staged-overlay payload.
 * @param {object} overlay - response from /api/overlay_staged.php
 * @returns {object} view-model
 */
export function buildLcsViewModel(overlay) {
  const o = overlay || {};
  const meta = o.meta || {};
  const actualMap = o.actual || {};
  const forecastMap = o.forecast || {};
  const paramsMap = o.params || {};
  const metricsMap = o.metrics || {};
  const anomMap = o.anomalies || {};

  const streamProfiles = STREAMS.map((name) => {
    const actual = profileStreamActual(actualMap[name]);
    const forecast = profileStreamForecast(forecastMap[name]);
    const params = paramsMap[name] || null;
    const metrics = metricsMap[name] || null;
    const anomalies = Array.isArray(anomMap[name]) ? anomMap[name] : [];
    const present = !!forecast;
    const reason = !actual || actual.count === 0
      ? "no_data"
      : actual.allZero
        ? "inactive"
        : !forecast
          ? "no_forecast"
          : params?.method === "sparse_intermittent"
            ? "sparse"
            : "ok";
    const confidence = streamConfidence({ actual, forecast, params, metrics });
    return {
      name,
      present,
      reason,
      actual,
      forecast,
      params,
      metrics,
      anomalies,
      confidence,
      ranking: {
        score: streamRankingScore({ actual, forecast, params, metrics, name }),
        label: streamRankingLabel({ actual, forecast, params, name }),
      },
    };
  });

  const ageD = ageDays(meta.created_at);
  const modelInfo = {
    runId: meta.run_id || null,
    snapshotId: meta.snapshot_id || null,
    createdAt: meta.created_at || null,
    apiVersion: meta.api_version || null,
    modelVersion: meta.model_version || null,
    source: meta.source || null,
    forecastWindow: {
      start: meta.forecast_start || null,
      end: meta.forecast_end || null,
      horizonDays: Number(meta.horizon) || null,
    },
    staleness: ageD == null ? null : {
      ageDays: ageD,
      severity: ageD > 30 ? "stale" : ageD > 7 ? "warn" : "fresh",
    },
  };

  const decisionContext = buildDecisionContext(streamProfiles);
  const caveats = buildCaveats({ modelInfo, streamProfiles, ok: o.ok !== false });

  return {
    lease: meta.lease || o.lease || null,
    tenant: meta.tenant_id || meta.run_tenant_id || null,
    modelInfo,
    streams: streamProfiles,
    decisionContext,
    caveats,
    raw: { actual: actualMap, forecast: forecastMap, anomalies: anomMap, params: paramsMap, metrics: metricsMap, meta },
  };
}

/* ───────────────────────────────────────────────────────────────────────────
 * buildRealLeaseFromView — convert a normalized view-model into an object
 * that econEngine.computeEconWindow accepts (it expects a `lease.chart` with
 * .oil/.gas/.water/.todayIdx arrays + name/basin/operator/econDefaults).
 *
 * actual + forecast.p50 are concatenated per stream. If a stream is absent,
 * we emit a zero array of the same length as the longest present stream so
 * the chart math doesn't blow up.
 * ──────────────────────────────────────────────────────────────────────── */
function _zip(actualRows, forecastRows) {
  // actualRows: [{date,q}], forecastRows: [{date,p50,...}]
  const a = (actualRows || []).map((p) => (Number.isFinite(p?.q) ? p.q : 0));
  const f = (forecastRows || []).map((p) => (Number.isFinite(p?.p50) ? p.p50 : 0));
  return { history: a, forecast: f };
}

export function buildRealLeaseFromView(view, options = {}) {
  if (!view || !view.raw) return null;
  const raw = view.raw;
  const oilZ = _zip(raw.actual?.Oil, raw.forecast?.Oil);
  const gasZ = _zip(raw.actual?.Gas, raw.forecast?.Gas);
  const watZ = _zip(raw.actual?.Water, raw.forecast?.Water);

  const histLen = Math.max(oilZ.history.length, gasZ.history.length, watZ.history.length);
  const fcstLen = Math.max(oilZ.forecast.length, gasZ.forecast.length, watZ.forecast.length);

  const padHist = (arr) => {
    if (arr.length === histLen) return arr;
    return [...new Array(histLen - arr.length).fill(0), ...arr];
  };
  const padFcst = (arr) => {
    if (arr.length === fcstLen) return arr;
    return [...arr, ...new Array(fcstLen - arr.length).fill(0)];
  };

  const oil = [...padHist(oilZ.history), ...padFcst(oilZ.forecast)];
  const gas = [...padHist(gasZ.history), ...padFcst(gasZ.forecast)];
  const water = [...padHist(watZ.history), ...padFcst(watZ.forecast)];
  const todayIdx = histLen;

  // Last 7 averages (positives) for summary fields used by some demo cards.
  const meanPos = (arr) => {
    const pos = (arr || []).filter((v) => Number.isFinite(v) && v > 0);
    return pos.length ? pos.reduce((s, v) => s + v, 0) / pos.length : 0;
  };

  const decisionWhy = view.decisionContext?.actionWhy || "";
  const decisionLabel = view.decisionContext?.suggestedAction || "";

  return {
    id: view.lease,
    name: options.displayName || view.lease,
    basin: options.basin || "LCS",
    operator: options.operator || "LCS",
    score: 0,
    tags: ["LCS", "REAL"],
    econDefaults: options.econDefaults || undefined,
    chart: {
      oil, gas, water, todayIdx,
      // Minimal SVG/curve data omitted — EconModal does not require it; only
      // the .oil/.gas/.water arrays are read by computeEconWindow.
      paths: { oilH: "", gasH: "", watH: "", oilF: "", gasF: "", watF: "",
               oilMA: "", gasMA: "", watMA: "",
               oilBandU: "", oilBandL: "", gasBandU: "", gasBandL: "", watBandU: "", watBandL: "" },
      todayX: 0,
      curveDefs: [],
      latestOil: oil[todayIdx - 1] ?? 0,
      latestGas: gas[todayIdx - 1] ?? 0,
      latestWater: water[todayIdx - 1] ?? 0,
      avg7Oil: meanPos(oil.slice(Math.max(0, todayIdx - 7), todayIdx)),
      avg7Gas: meanPos(gas.slice(Math.max(0, todayIdx - 7), todayIdx)),
      avg7Water: meanPos(water.slice(Math.max(0, todayIdx - 7), todayIdx)),
      fcst7Oil: oil.slice(todayIdx, todayIdx + 7).reduce((s, v) => s + (v || 0), 0),
      fcst7Gas: gas.slice(todayIdx, todayIdx + 7).reduce((s, v) => s + (v || 0), 0),
    },
    fieldNotes: [],
    opsFlags: [],
    annotations: [],
    inference: {
      signal: decisionLabel,
      diagnosis: decisionWhy || "Live LCS data — see right rail decision card.",
      directive: decisionLabel ? "→ " + decisionLabel : "",
      summary: decisionWhy,
      whatMatters: (view.caveats || [])[0] || "",
      action: decisionLabel,
      econRead: "",
      confidence: view.decisionContext?.streamConfidences || { Oil: "n/a", Gas: "n/a", Water: "n/a" },
      oneLiner: `"${decisionLabel}" — ${view.lease}`,
      evidence: view.caveats || [],
    },
    isRealLcs: true,
    _viewModel: view,
  };
}

export default buildLcsViewModel;
